import type { Route } from "@route-types/+types/home.$pub";
import { ResponseType } from "@sphtech/web2-core/ssr";
import assertEnum from "@web2/web2-helpers/assertEnum";
import type { ClientLoaderFunctionArgs } from "react-router";
import { useLoaderData, useParams } from "react-router";

import {
  PublicationPathName,
  PublicationShortname,
} from "../auth/serviceIdentifier";
import ErrorPage from "../pages/ErrorPage";
import HomePage from "../pages/Home";
import { Language } from "../types/Data.d";

const BASE_URL =
  import.meta.env.VITE_SITE_BASE_URL ?? "https://e-paper.sph.com.sg";

type SeoConfig = {
  title: string;
  description: string;
  canonical: string;
  h1: string;
};

const PUB_SEO_CONFIG: Record<string, SeoConfig> = {
  st: {
    title: "The Straits Times E-Paper - Read Today's Edition",
    description:
      "Read the epaper of The Straits Times. Stay updated with the latest news, business, and lifestyle stories from Singapore and around the world.",
    canonical: `${BASE_URL}/st`,
    h1: "The Straits Times E-Paper",
  },
  bt: {
    title: "The Business Times E-Paper - Daily Financial News",
    description:
      "Access The Business Times e-paper for comprehensive daily coverage of financial markets, business news, and corporate insights in Asia.",
    canonical: `${BASE_URL}/bt`,
    h1: "The Business Times E-Paper",
  },
  bh: {
    title: "Berita Harian E-Paper - Edisi Digital",
    description:
      "Baca edisi digital Berita Harian. Dapatkan berita terkini, gaya hidup, dan hiburan untuk masyarakat Melayu/Islam di Singapura.",
    canonical: `${BASE_URL}/bh`,
    h1: "Berita Harian E-Paper",
  },
  zb: {
    title: "《联合早报》电子报 (Lianhe Zaobao E-Paper)",
    description:
      "阅读《联合早报》电子报。掌握新加坡、亚洲及全球的最新新闻、深度报道与评论分析。",
    canonical: `${BASE_URL}/zb`,
    h1: "《联合早报》电子报",
  },
  tm: {
    title: "Tamil Murasu E-Paper - தமிழ் முரசு மின்-இதழ்",
    description:
      "தமிழ் முரசு மின்-இதழைப் படியுங்கள். சிங்கப்பூர் மற்றும் உலகளாவிய செய்திகளை உடனுக்குடன் தெரிந்துகொள்ளுங்கள்.",
    canonical: `${BASE_URL}/tm`,
    h1: "தமிழ் முரசு மின்-இதழ்",
  },
  tabla: {
    title: "tabla! E-Paper - News for the Indian Diaspora",
    description:
      "Read the tabla! e-paper. Your weekly digital guide to news, views, and entertainment for the Indian diaspora in Singapore.",
    canonical: `${BASE_URL}/tabla`,
    h1: "tabla! E-Paper",
  },
  bm: {
    title: "SPH E-Paper",
    description:
      "Read the latest edition of your favourite SPH newspaper online.",
    canonical: `${BASE_URL}/bm`,
    h1: "SPH E-Paper",
  },
  mm: {
    title: "SPH E-Paper",
    description:
      "Read the latest edition of your favourite SPH newspaper online.",
    canonical: `${BASE_URL}/mm`,
    h1: "SPH E-Paper",
  },
  sm: {
    title: "《新明日报》电子报",
    description:
      "阅读《新明日报》电子报，为你报道新加坡民生课题、社会新闻及贴近生活的大小事。",
    canonical: `${BASE_URL}/sm`,
    h1: "《新明日报》电子报",
  },
};

const DEFAULT_SEO: SeoConfig = {
  title: "SPH E-Paper",
  description:
    "Read the latest edition of your favourite SPH newspaper online.",
  canonical: BASE_URL,
  h1: "SPH E-Paper",
};

function getSeoConfig(pub: string): SeoConfig {
  return (
    PUB_SEO_CONFIG[pub] ?? {
      ...DEFAULT_SEO,
      canonical: `${BASE_URL}/${pub}`,
      h1: `${pub.toUpperCase()} E-Paper`,
    }
  );
}

const PUBLICATION_CONFIG: Record<
  string,
  {
    lang: Language;
    secondaryPublicationId?: PublicationShortname;
    navTabBarIndex?: number;
  }
> = {
  st: { lang: Language.en },
  bt: { lang: Language.en },
  zb: {
    lang: Language.zh,
    secondaryPublicationId: PublicationShortname.SM,
    navTabBarIndex: 1,
  },
  bh: { lang: Language.ms },
  tm: {
    lang: Language.ta,
    secondaryPublicationId: PublicationShortname.TABLA,
    navTabBarIndex: 1,
  },
  bm: { lang: Language.ta },
  mm: { lang: Language.ta },
  tabla: {
    lang: Language.en,
    secondaryPublicationId: PublicationShortname.TM,
    navTabBarIndex: 2,
  },
  sm: {
    lang: Language.zh,
    secondaryPublicationId: PublicationShortname.ZB,
    navTabBarIndex: 2,
  },
};

export function meta({ params }: Route.MetaArgs) {
  const pub = params.pub ?? "";
  const isValidPub = !!PUBLICATION_CONFIG[pub];

  if (!isValidPub) {
    return [
      { title: "Page Not Found" },
      { name: "robots", content: "noindex, nofollow" },
    ];
  }

  const seo = getSeoConfig(pub);

  return [
    { title: seo.title },
    { name: "description", content: seo.description },
    { name: "robots", content: "index, follow" },
    { tagName: "link", rel: "canonical", href: seo.canonical },
    { property: "og:type", content: "website" },
    { property: "og:url", content: seo.canonical },
    { property: "og:title", content: seo.title },
    { property: "og:description", content: seo.description },
  ];
}

export default function PublicationRoute() {
  const { pub } = useParams();
  const loaderData = useLoaderData();

  if (!pub || !PUBLICATION_CONFIG[pub]) {
    return <ErrorPage response={{ statusCode: 404 }} />;
  }

  const config = PUBLICATION_CONFIG[pub];
  const seo = getSeoConfig(pub);
  return (
    <HomePage
      lang={config.lang}
      h1Text={seo.h1}
      secondaryPublicationId={config.secondaryPublicationId ?? null}
      navTabBarIndex={config.navTabBarIndex ?? null}
      context={loaderData}
    />
  );
}

export async function loader({ params, context: ctx }: Route.LoaderArgs) {
  const pub = params.pub ?? "";
  if (!PUBLICATION_CONFIG[pub]) {
    return {
      type: ResponseType.CLIENT_ERROR,
      statusCode: 404,
      lang: Language.en,
    };
  }
  const context = ctx as Record<string, unknown>;
  const lang = PUBLICATION_CONFIG[params.pub ?? ""].lang ?? Language.en;

  if (context.pageLoaderResponse) {
    return { ...(context.pageLoaderResponse as object), lang };
  }

  if (context.previewData) {
    const previewData = context.previewData as { context: unknown };
    return {
      type: ResponseType.SUCCESS,
      statusCode: 200,
      payload: previewData.context,
      lang,
    };
  }

  const pubCode = assertEnum(PublicationShortname, params.pub ?? "");
  if (pubCode !== null) {
    return {
      type: ResponseType.SUCCESS,
      statusCode: 200,
      payload: {
        pubId: pubCode,
        path: pubCode as unknown as PublicationPathName,
      },
      lang,
    };
  }
  return { type: ResponseType.CLIENT_ERROR, statusCode: 404, lang };
}

export async function clientLoader(args: Route.ClientLoaderArgs) {
  const { fetchContext } = await import("@sphtech/web2-core/ssr");
  const result = (await fetchContext(
    args as unknown as ClientLoaderFunctionArgs,
  )) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult;
}
